import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import RichContent from '@/components/RichContent';
import {
  Plus, Send, Trash2, Settings, CreditCard, Sun, Moon, Menu, X, LogOut, User,
  Loader2, Sparkles, ChevronRight, MessageSquare, FileText, HelpCircle,
  Download, FileSpreadsheet, FileIcon, Presentation, Expand, Minimize2, Paperclip, ImageIcon,
  Table2, Wand2, Zap, Network, Cloud, Server, Shield, Radio, Cpu,
  ChevronDown, ArrowUpRight, Activity, Gauge, Database
} from 'lucide-react';

// ==================== WORKFLOW SHORTCUTS ====================
const WORKFLOW_SHORTCUTS = [
  { id: 'ran-sop', label: '5G RAN Deployment SOP', icon: Radio, query: 'Generate a detailed 5G RAN site deployment SOP covering site preparation, hardware installation, commissioning, integration testing, and handover checklist. Output in WORD MODE.' },
  { id: 'k8s-migration', label: 'K8s Migration Plan', icon: Cloud, query: 'Create a Kubernetes cluster migration plan covering pre-migration assessment, workload inventory, dependency mapping, migration phases, rollback strategy, and validation checklist. Output in WORD MODE.' },
  { id: 'network-capacity', label: 'Network Capacity Plan', icon: Network, query: 'Generate a network capacity planning template covering current utilization metrics, growth projections, threshold definitions, upgrade triggers, and budget estimation. Output in EXCEL MODE.' },
  { id: 'oran-assessment', label: 'ORAN Architecture Review', icon: Cpu, query: 'Create an O-RAN architecture assessment framework covering RIC evaluation, xApp compatibility, fronthaul latency analysis, security posture, and vendor interoperability matrix. Output in WORD MODE.' },
  { id: 'cloud-audit', label: 'Cloud Infra Audit', icon: Server, query: 'Generate a cloud infrastructure audit checklist covering compute, storage, networking, security, IAM, cost optimization, compliance, and disaster recovery. Output in EXCEL MODE.' },
  { id: 'security-playbook', label: 'Security Playbook', icon: Shield, query: 'Create a network security incident response playbook covering detection, triage, containment, eradication, recovery, and post-incident review. Output in WORD MODE.' },
];

// ==================== REFINEMENT ACTIONS ====================
const REFINEMENT_ACTIONS = [
  { id: 'expand', label: 'Expand', icon: Expand, prompt: 'Expand the previous response with more technical detail, additional steps, and deeper explanations. Keep the same format.' },
  { id: 'simplify', label: 'Simplify', icon: Minimize2, prompt: 'Simplify the previous response. Use shorter sentences, remove jargon, and make it accessible to a junior engineer.' },
  { id: 'table', label: 'Table Format', icon: Table2, prompt: 'Convert the previous response into a structured table format with clear columns. Output in EXCEL MODE.' },
  { id: 'technical', label: 'More Technical', icon: Wand2, prompt: 'Rewrite the previous response with deeper technical depth. Include specific protocols, configurations, CLI commands, and parameters where applicable.' },
];

// ==================== EXPORT BUTTON COMPONENT ====================
const ExportButtons = function({ content, title }) {
  const [exporting, setExporting] = useState('');

  const handleExport = async function(format) {
    setExporting(format);
    try {
      const blob = await api.exportDocument(content, title || 'QueryVerse Export', format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extensions = { docx: '.docx', xlsx: '.xlsx', pptx: '.pptx' };
      a.download = (title || 'QueryVerse Export').substring(0, 50) + (extensions[format] || '.docx');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Document exported successfully');
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border/30" data-testid="export-buttons">
      <span className="text-xs text-muted-foreground mr-1">Export:</span>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={function() { handleExport('docx'); }}
              disabled={!!exporting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
              data-testid="export-docx-btn"
            >
              {exporting === 'docx' ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
              Word
            </button>
          </TooltipTrigger>
          <TooltipContent>Download as .docx</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={function() { handleExport('xlsx'); }}
              disabled={!!exporting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
              data-testid="export-xlsx-btn"
            >
              {exporting === 'xlsx' ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
              Excel
            </button>
          </TooltipTrigger>
          <TooltipContent>Download as .xlsx</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={function() { handleExport('pptx'); }}
              disabled={!!exporting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-orange-500/10 text-orange-600 dark:text-orange-400 hover:bg-orange-500/20 transition-colors disabled:opacity-50"
              data-testid="export-pptx-btn"
            >
              {exporting === 'pptx' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Presentation className="h-3 w-3" />}
              PowerPoint
            </button>
          </TooltipTrigger>
          <TooltipContent>Download as .pptx</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

// ==================== USAGE ODOMETER COMPONENT ====================
const UsageOdometer = function({ user, onUpgrade }) {
  if (!user) return null;

  const queriesUsed = user.messages_today || 0;
  const queriesLimit = user.messages_limit || 10;
  const queriesPercent = Math.min((queriesUsed / queriesLimit) * 100, 100);
  const remaining = Math.max(0, queriesLimit - queriesUsed);

  var statusColor = 'text-green-500';
  var statusBg = 'bg-green-500';
  var statusLabel = 'Healthy';
  if (queriesPercent >= 90) {
    statusColor = 'text-red-500';
    statusBg = 'bg-red-500';
    statusLabel = 'Critical';
  } else if (queriesPercent >= 70) {
    statusColor = 'text-amber-500';
    statusBg = 'bg-amber-500';
    statusLabel = 'Warning';
  }

  return (
    <div className="px-3 py-3 rounded-xl bg-card border border-border/50 space-y-3" data-testid="usage-odometer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Usage</span>
        </div>
        <div className={'flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ' + statusColor + ' bg-opacity-10 ' + statusBg + '/10'}>
          <span className={'h-1.5 w-1.5 rounded-full ' + statusBg} />
          {statusLabel}
        </div>
      </div>

      {/* Query counter */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-2xl font-bold tabular-nums">{remaining}</span>
          <span className="text-xs text-muted-foreground">of {queriesLimit} left</span>
        </div>
        <Progress value={queriesPercent} className="h-2" data-testid="usage-progress-bar" />
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-muted-foreground">{queriesUsed} used</span>
          <span className="text-[10px] text-muted-foreground">{queriesLimit} max</span>
        </div>
      </div>

      {/* Plan info */}
      <div className="flex items-center justify-between pt-2 border-t border-border/30">
        <span className="text-xs text-muted-foreground capitalize">{user.subscription_plan || 'Free'} Plan</span>
        {user.subscription_plan === 'free' && (
          <button
            onClick={onUpgrade}
            className="text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-0.5 transition-colors"
            data-testid="upgrade-odometer-btn"
          >
            Upgrade <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== COST ESTIMATOR COMPONENT ====================
const CostEstimator = function({ estimate }) {
  if (!estimate) return null;

  var colorClass = 'text-green-500 bg-green-500/10';
  var label = 'Low';
  if (estimate.complexity === 'high') {
    colorClass = 'text-orange-500 bg-orange-500/10';
    label = 'High';
  } else if (estimate.complexity === 'medium') {
    colorClass = 'text-amber-500 bg-amber-500/10';
    label = 'Medium';
  }

  return (
    <div className="flex items-center gap-2 px-1 py-1" data-testid="cost-estimator">
      <div className={'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ' + colorClass}>
        <Gauge className="h-3 w-3" />
        {label} complexity
      </div>
      <span className="text-[11px] text-muted-foreground">
        ~{estimate.estimated_output_tokens} tokens
      </span>
      {!estimate.within_budget && (
        <span className="text-[11px] text-red-500 font-medium">May exceed budget</span>
      )}
    </div>
  );
};

// ==================== MAIN CHAT PAGE ====================
const ChatPage = () => {
  const { conversationId } = useParams();
  const location = useLocation();
  const { user, token, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false);
  const [costEstimate, setCostEstimate] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const estimateTimerRef = useRef(null);

  // Handle pending query from landing page
  useEffect(() => {
    if (token && !initialQueryProcessed) {
      const pendingQuery = sessionStorage.getItem('pendingQuery');
      const stateQuery = location.state?.initialQuery;
      
      if (pendingQuery || stateQuery) {
        const queryToSend = pendingQuery || stateQuery;
        sessionStorage.removeItem('pendingQuery');
        setInputValue(queryToSend);
        setInitialQueryProcessed(true);
        
        setTimeout(() => {
          if (queryToSend) {
            submitInitialQuery(queryToSend);
          }
        }, 500);
      }
    }
  }, [token, initialQueryProcessed, location.state]); // eslint-disable-line react-hooks/exhaustive-deps -- submitInitialQuery is intentionally not re-fired on identity change; this effect runs once per route navigation.

  const processStream = async function(response, convId) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    var fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].startsWith('data: ')) {
          try {
            const data = JSON.parse(lines[i].slice(6));
            if (data.delta) {
              fullResponse += data.delta;
              setStreamingMessage(fullResponse);
            } else if (data.content) {
              fullResponse = data.content;
              setStreamingMessage(fullResponse);
            }
          } catch (e) {}
        }
      }
    }
    return fullResponse;
  };

  const submitInitialQuery = async (queryText) => {
    if (!queryText.trim() || sending) return;
    setInputValue('');
    
    try {
      const conv = await api.createConversation();
      setConversations(function(prev) { return [conv].concat(prev); });
      navigate('/chat/' + conv.id, { replace: true });
      
      const userMessage = {
        id: 'temp-' + Date.now(),
        role: 'user',
        content: queryText.trim(),
        created_at: new Date().toISOString()
      };
      setMessages([userMessage]);
      setSending(true);
      
      const response = await api.sendMessage(conv.id, queryText.trim());
      const fullResponse = await processStream(response, conv.id);

      const assistantMessage = {
        id: 'msg-' + Date.now(),
        role: 'assistant',
        content: fullResponse,
        created_at: new Date().toISOString()
      };
      setMessages(function(prev) { return prev.concat([assistantMessage]); });
      setStreamingMessage('');
      loadConversations();
      refreshUser();
    } catch (error) {
      toast.error(error.message || 'Failed to process query');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadConversations();
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadConversations and refreshUser are stable refs from context; including them would cause an infinite refresh loop on every render.
  }, [token]);

  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
    } else {
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  // Debounced cost estimation
  useEffect(() => {
    if (estimateTimerRef.current) {
      clearTimeout(estimateTimerRef.current);
    }
    
    if (!inputValue.trim() || inputValue.trim().length < 10) {
      setCostEstimate(null);
      return;
    }

    estimateTimerRef.current = setTimeout(async () => {
      try {
        const est = await api.estimateCost(inputValue.trim());
        setCostEstimate(est);
      } catch (e) {
        setCostEstimate(null);
      }
    }, 600);

    return () => {
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    };
  }, [inputValue]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const data = await api.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadMessages = async (convId) => {
    setLoading(true);
    try {
      const data = await api.getMessages(convId);
      setMessages(data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const createNewConversation = async () => {
    try {
      const conv = await api.createConversation();
      setConversations(function(prev) { return [conv].concat(prev); });
      navigate('/chat/' + conv.id);
      setSidebarOpen(false);
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.deleteConversation(convId);
      setConversations(function(prev) { return prev.filter(function(c) { return c.id !== convId; }); });
      if (conversationId === convId) {
        navigate('/chat');
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const handleAttachmentSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (attachments.length >= 5) {
      toast.error('Maximum 5 attachments per message');
      return;
    }
    setUploadingAttachment(true);
    try {
      const att = await api.uploadChatAttachment(file);
      setAttachments(function(prev) { return prev.concat([att]); });
      toast.success('"' + att.filename + '" attached');
    } catch (error) {
      toast.error(error.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = (id) => {
    setAttachments(function(prev) { return prev.filter(function(a) { return a.id !== id; }); });
  };

  const sendMessage = async (overrideContent) => {
    const content = overrideContent || inputValue;
    if (!content.trim() || sending) return;

    var activeConvId = conversationId;

    if (!activeConvId) {
      try {
        const conv = await api.createConversation();
        setConversations(function(prev) { return [conv].concat(prev); });
        activeConvId = conv.id;
        navigate('/chat/' + conv.id, { replace: true });
      } catch (error) {
        toast.error('Failed to create conversation');
        return;
      }
    }

    const attachmentIds = attachments.map(function(a) { return a.id; });
    const userMessage = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: content.trim(),
      attachments: attachments.map(function(a) { return { filename: a.filename, kind: a.kind }; }),
      created_at: new Date().toISOString()
    };

    setMessages(function(prev) { return prev.concat([userMessage]); });
    if (!overrideContent) setInputValue('');
    setAttachments([]);
    setSending(true);
    setStreamingMessage('');
    setCostEstimate(null);

    try {
      const response = await api.sendMessage(activeConvId, userMessage.content, attachmentIds);
      const fullResponse = await processStream(response, activeConvId);

      const assistantMessage = {
        id: 'msg-' + Date.now(),
        role: 'assistant',
        content: fullResponse,
        created_at: new Date().toISOString()
      };

      setMessages(function(prev) { return prev.concat([assistantMessage]); });
      setStreamingMessage('');
      loadConversations();
      refreshUser();

    } catch (error) {
      if (error.message && error.message.indexOf('limit') !== -1) {
        toast.error('Daily query limit reached. Upgrade your plan for more.', {
          action: { label: 'Upgrade', onClick: function() { navigate('/pricing'); } }
        });
      } else {
        toast.error(error.message || 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const handleRefinement = function(action) {
    sendMessage(action.prompt);
  };

  const handleWorkflowShortcut = function(shortcut) {
    sendMessage(shortcut.query);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const canSendMessage = () => {
    if (!user) return false;
    return user.messages_today < user.messages_limit;
  };

  const messagesRemaining = () => {
    if (!user) return 0;
    return Math.max(0, (user.messages_limit || 10) - (user.messages_today || 0));
  };

  // Pre-build conversation list items to avoid .map inside JSX
  const convListItems = [];
  for (var ci = 0; ci < conversations.length; ci++) {
    const conv = conversations[ci];
    convListItems.push(
      <div
        key={conv.id}
        onClick={((c) => () => { navigate('/chat/' + c.id); setSidebarOpen(false); })(conv)}
        className={'w-full px-3 py-2.5 rounded-xl text-left text-sm flex items-center justify-between gap-2 group transition-colors duration-200 cursor-pointer ' + (conversationId === conv.id ? 'bg-primary/10 text-foreground border border-primary/20' : 'hover:bg-secondary/50 text-muted-foreground hover:text-foreground')}
        data-testid={'conversation-' + conv.id}
      >
        <span className="truncate flex-1 flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />
          {conv.title}
        </span>
        <button
          onClick={((c) => (e) => { e.stopPropagation(); deleteConversation(c.id, e); })(conv)}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded transition-all"
          data-testid={'delete-conv-' + conv.id}
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </button>
      </div>
    );
  }

  // Pre-build workflow shortcut buttons
  const workflowButtons = [];
  for (var wi = 0; wi < WORKFLOW_SHORTCUTS.length; wi++) {
    const shortcut = WORKFLOW_SHORTCUTS[wi];
    const IconComp = shortcut.icon;
    workflowButtons.push(
      <button
        key={shortcut.id}
        onClick={((s) => () => handleWorkflowShortcut(s))(shortcut)}
        disabled={sending || !canSendMessage()}
        className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-card border border-border/50 hover:border-primary/30 hover:bg-primary/5 transition-all duration-200 text-left group disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={'shortcut-' + shortcut.id}
      >
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
          <IconComp className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{shortcut.label}</p>
          <p className="text-[10px] text-muted-foreground truncate">One-click structured output</p>
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0" />
      </button>
    );
  }

  // Pre-build message list
  const messageElements = [];
  for (var mi = 0; mi < messages.length; mi++) {
    const msg = messages[mi];
    const isUser = msg.role === 'user';
    const isLastAssistant = !isUser && mi === messages.length - 1;
    const convTitle = conversationId ? (conversations.find(function(c) { return c.id === conversationId; }) || {}).title : '';

    messageElements.push(
      <div key={msg.id} className={'flex ' + (isUser ? 'justify-end' : 'justify-start') + ' animate-fade-in'} style={{ animationDelay: (mi * 50) + 'ms' }}>
        <div className={'max-w-[85%] ' + (isUser ? '' : 'flex gap-3')}>
          {!isUser && (
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
          )}
          <div className={isUser ? '' : 'flex-1'}>
            <div className={'px-5 py-3 rounded-2xl ' + (isUser ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-card border border-border/50 rounded-tl-sm')}>
              {isUser ? (
                <div>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {msg.attachments.map(function(att, ai) {
                        return (
                          <span key={ai} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-foreground/15 text-xs">
                            {att.kind === 'image' ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                            <span className="max-w-[160px] truncate">{att.filename}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              ) : (
                <RichContent content={msg.content} />
              )}
            </div>
            
            {/* Export buttons + refinement for assistant messages */}
            {!isUser && (
              <div className="mt-2 space-y-2">
                {/* Refinement buttons */}
                <div className="flex items-center gap-1 flex-wrap" data-testid="refinement-buttons">
                  {REFINEMENT_ACTIONS.map(function(action) {
                    const ActionIcon = action.icon;
                    return (
                      <button
                        key={action.id}
                        onClick={function() { handleRefinement(action); }}
                        disabled={sending || !canSendMessage()}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={'refine-' + action.id + '-btn'}
                      >
                        <ActionIcon className="h-3 w-3" />
                        {action.label}
                      </button>
                    );
                  })}
                </div>
                
                {/* Export buttons */}
                <ExportButtons content={msg.content} title={convTitle || 'QueryVerse Export'} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={'fixed md:relative inset-y-0 left-0 z-50 w-72 bg-card border-r border-border/50 transform transition-transform duration-300 ease-out flex flex-col ' + (sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-border/50">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/queryverse-logo.png" alt="QueryVerse" className="h-10" />
              <span className="text-[10px] text-muted-foreground hidden md:block italic">a product by AppCenn Pvt Ltd</span>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="md:hidden p-2 rounded-full hover:bg-secondary transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <Button onClick={createNewConversation} className="w-full mt-4 rounded-full justify-start gap-2 glow-sm" data-testid="new-chat-btn">
            <Plus className="h-4 w-4" />
            New Query
          </Button>
          <Button onClick={() => navigate('/knowledge-base')} variant="outline" className="w-full mt-2 rounded-full justify-start gap-2" data-testid="knowledge-base-btn">
            <Database className="h-4 w-4" />
            Knowledge Base
          </Button>
        </div>

        {/* Conversations List */}
        <ScrollArea className="flex-1 px-2 py-2">
          <p className="px-2 text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Recent Queries</p>
          <div className="space-y-1">
            {convListItems}
            
            {conversations.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p>No queries yet</p>
                <p className="text-xs mt-1">Start a new query to begin</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border/50 space-y-3">
          {/* Enhanced Usage Odometer */}
          <UsageOdometer user={user} onUpgrade={() => navigate('/pricing')} />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full px-3 py-2.5 rounded-xl hover:bg-secondary/50 flex items-center gap-3 transition-colors" data-testid="user-menu-btn">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user && user.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{(user && user.subscription_plan) || 'Free'} Plan</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {user && user.is_admin && (
                <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                  <Shield className="h-4 w-4 mr-2" /> Admin Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="billing-menu-item">
                <CreditCard className="h-4 w-4 mr-2" /> Billing
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-menu-item">
                {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-menu-item">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <header className="glass border-b border-border/50 px-4 py-3 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 rounded-full hover:bg-secondary transition-colors" data-testid="mobile-sidebar-btn">
            <Menu className="h-5 w-5" />
          </button>
          
          <div className="flex-1">
            <h1 className="font-semibold truncate">
              {conversationId ? (conversations.find(function(c) { return c.id === conversationId; }) || {}).title || 'Query' : 'New Query'}
            </h1>
            <p className="text-xs text-muted-foreground">Telecom &bull; Cloud &bull; AI-RAN Engineering Intelligence</p>
          </div>

          {/* Powered by Appcenn */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:inline">Powered by</span>
            <img src="/appcenn-logo.jpg" alt="Appcenn" className="h-6 rounded" />
          </div>
        </header>

        {/* Messages Area */}
        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 && !conversationId ? (
              <div className="py-8">
                {/* Welcome header */}
                <div className="text-center mb-10">
                  <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5 animate-pulse-glow">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight">
                    Engineering <span className="gradient-text">Intelligence</span> Workspace
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                    Ask questions or use workflow shortcuts for instant structured outputs.
                  </p>
                </div>

                {/* Workflow Shortcuts Grid */}
                <div className="mb-8" data-testid="workflow-shortcuts">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">Quick Workflows</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {workflowButtons}
                  </div>
                </div>

                {/* Quick query suggestions */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-3">Or type a custom query</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {['RAN cell outage troubleshooting', '5G Core deployment', 'Kubernetes networking', 'ORAN fronthaul latency'].map(function(suggestion) {
                      return (
                        <button key={suggestion} onClick={function() { setInputValue(suggestion); }} className="px-4 py-2 rounded-full bg-secondary/50 hover:bg-secondary text-sm transition-colors border border-border/50" data-testid={'suggestion-' + suggestion.replace(/\s+/g, '-').toLowerCase()}>
                          {suggestion}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <React.Fragment>
                {messageElements}
                
                {/* Streaming message */}
                {streamingMessage && (
                  <div className="flex justify-start animate-fade-in">
                    <div className="max-w-[85%] flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                      </div>
                      <div className="px-5 py-3 rounded-2xl bg-card border border-border/50 rounded-tl-sm">
                        <p className="whitespace-pre-wrap leading-relaxed">{streamingMessage}</p>
                        <span className="inline-block w-2 h-4 bg-primary/50 animate-pulse ml-0.5" />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Typing indicator */}
                {sending && !streamingMessage && (
                  <div className="flex justify-start">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                      </div>
                      <div className="px-5 py-4 rounded-2xl bg-card border border-border/50 rounded-tl-sm">
                        <div className="flex gap-1.5">
                          <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t border-border/50 bg-background p-4">
          <div className="max-w-3xl mx-auto">
            {!canSendMessage() && user && (
              <div className="mb-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20 text-sm flex items-center justify-between" data-testid="limit-reached-banner">
                <span className="text-muted-foreground">Daily limit reached ({user.messages_limit} queries)</span>
                <Button size="sm" onClick={() => navigate('/pricing')} className="rounded-full" data-testid="upgrade-limit-btn">Upgrade</Button>
              </div>
            )}
            
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="attachment-chips">
                {attachments.map(function(att) {
                  return (
                    <div key={att.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs">
                      {att.kind === 'image' ? <ImageIcon className="h-3.5 w-3.5 text-primary" /> : <FileText className="h-3.5 w-3.5 text-primary" />}
                      <span className="max-w-[180px] truncate">{att.filename}</span>
                      <button onClick={function() { removeAttachment(att.id); }} className="text-muted-foreground hover:text-foreground">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.docx,.pptx,.xlsx,.xls,.csv"
                onChange={handleAttachmentSelect}
                className="hidden"
                data-testid="attachment-input"
              />
              <Textarea
                value={inputValue}
                onChange={function(e) { setInputValue(e.target.value); }}
                onKeyDown={handleKeyDown}
                placeholder={canSendMessage() ? "Ask about RAN, Core, Cloud, Kubernetes, AI-RAN, ORAN..." : "Upgrade to continue..."}
                disabled={!canSendMessage() || sending}
                className="min-h-[56px] max-h-[200px] pl-14 pr-14 rounded-2xl resize-none bg-secondary/30 border-border/50 focus:border-primary/50 focus:ring-primary/20"
                rows={1}
                data-testid="chat-input"
              />
              <Button
                onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                disabled={!canSendMessage() || sending || uploadingAttachment}
                size="icon"
                variant="ghost"
                className="absolute left-2 bottom-2 rounded-full h-10 w-10 text-muted-foreground hover:text-foreground"
                title="Attach an image or document"
                data-testid="attach-file-btn"
              >
                {uploadingAttachment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <Button
                onClick={function() { sendMessage(); }}
                disabled={!inputValue.trim() || !canSendMessage() || sending}
                size="icon"
                className="absolute right-2 bottom-2 rounded-full h-10 w-10 glow-sm"
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            
            <div className="flex items-center justify-between mt-2">
              <CostEstimator estimate={costEstimate} />
              <p className="text-xs text-muted-foreground">
                {messagesRemaining()} queries remaining (max {user && user.messages_limit || 10}/day)
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChatPage;
